// Cross-tab session hand-off over `BroadcastChannel` (decision D10).
//
// The `storage` event carried this before the feature, and it cannot any more: nothing this
// package writes to Web Storage changes when a session does. One tab refreshes under the Web Lock
// and posts the fresh access token here; the others adopt it and skip a refresh of their own
// (security chain C3 step 4).
/** The `BroadcastChannel` name every tab of one origin opens. Exported for the tests (D10). */
export const SESSION_CHANNEL_NAME = 'zion-session';
/**
 * Is `value` one of the two messages this channel carries?
 *
 * ⛔ A RECEIVER VALIDATES; A CAST IS NO CHECK (R-46). The channel is same-origin and every hosted
 * app bundle runs in the shell's own document, so any of them can post anything on it. The
 * listener used to take `event.data` as a `SessionChannelMessage` on faith, and a malformed
 * message reached `startSessionSync`'s handler as a live value.
 *
 * ⛔ AND THE KEY SET IS EXACT, WHICH IS THE `.strict()` HALF. An extra key is a message this
 * version does not understand, so it is dropped rather than half-read. Written by hand rather
 * than with zod: `@zion/sdk-react` declares one dependency, `@zion/sdk-contracts`, and `zod`
 * does not resolve from this package (measured 2026-09-22); adding it is a dependency default F4
 * refuses without a ruling.
 *
 * It throws nothing and reads nothing outside `value`, so a hostile message costs one predicate.
 */
function isSessionChannelMessage(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const keys = Object.keys(value).sort();
    const record = value;
    if (record['type'] === 'signed-out')
        return keys.length === 1;
    if (record['type'] === 'token-refreshed') {
        return (keys.length === 2 &&
            keys[0] === 'accessToken' &&
            typeof record['accessToken'] === 'string');
    }
    return false;
}
/**
 * Open the session channel, or a channel that does nothing when the browser has no
 * `BroadcastChannel`. Callers never branch on which one they got: a tab without the API keeps
 * working and refreshes on its own schedule under `withRefreshLock`. Errors: none. Side effect: one
 * `BroadcastChannel` construction when the global exists.
 */
export function openSessionChannel() {
    // Read the constructor off the global rather than naming `BroadcastChannel` directly: a browser
    // that does not ship the API has no binding at all, and a bare reference would be a
    // `ReferenceError` instead of the no-op fallback this function promises.
    const Ctor = globalThis.BroadcastChannel;
    const channel = Ctor === undefined ? null : new Ctor(SESSION_CHANNEL_NAME);
    /**
     * Closed once `close` has run. The platform throws `InvalidStateError` from
     * `BroadcastChannel.postMessage` after `close()`, and `SessionChannel.post` promises to throw
     * nothing, so this flag is what keeps that promise (R-27, finding PA-3).
     */
    let closed = false;
    const listeners = new Set();
    const onMessage = (event) => {
        // Dropped here, so no listener ever sees a message this version does not understand (R-46).
        if (!isSessionChannelMessage(event.data))
            return;
        const message = event.data;
        // A snapshot, because a listener that unsubscribes while it runs would otherwise mutate the
        // Set mid-iteration. `Array.from` rather than a spread: `unicorn(no-useless-spread)` refuses
        // the spread and the package's oxlint ceiling is 7 warnings with no headroom.
        for (const listener of Array.from(listeners))
            listener(message);
    };
    channel?.addEventListener('message', onMessage);
    return {
        post(message) {
            if (closed || channel === null)
                return;
            channel.postMessage(message);
        },
        subscribe(listener) {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        close() {
            closed = true;
            listeners.clear();
            channel?.removeEventListener('message', onMessage);
            channel?.close();
        },
    };
}
//# sourceMappingURL=session-channel.js.map